import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import AppBar from 'components/AppBar';
import SubmitButton from 'components/Buttons/SubmitButton';
import Field from 'components/Field';
import FormikSubmitErrorEffect from 'components/FormikSubmitErrorEffect';
import Input from 'components/Input';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Section from 'components/Section';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { CostCenter } from 'models/costCenter';
import ResponseScreen, { BackgroundTypes } from 'screens/components/ResponseScreen';
import { extractErrorMessage, htmlT } from 'utils/generic';

import { Container, Form, Fields } from './styles';

interface Props {
  loading: boolean;
  submitting: boolean;
  costCenter?: CostCenter;
  countryCurrency: string;
  isFromSelection?: boolean;
  onConfirm: (costCenter: CostCenter) => Promise<void>;
  onCloseFormCostCenter?: () => void;
}

const CostCenterForm = ({
  loading,
  submitting,
  costCenter,
  countryCurrency,
  onConfirm,
  isFromSelection,
  onCloseFormCostCenter,
}: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const toast = useToast();
  const [showSuccess, setShowSuccess] = useState(false);

  const isEdit = !!costCenter && costCenter.id !== '';

  const validationSchema = yup.object().shape({
    name: yup.string().trim().min(3).max(100).required(),
    code: yup.string().trim().min(3).max(100),
  });

  const initialValues: CostCenter = {
    name: costCenter?.name || '',
    code: costCenter?.code || '',
    budget: `${costCenter?.budget}` || '0',
    currency: countryCurrency,
  };

  const handleConfirm = async (values: typeof initialValues) => {
    try {
      await onConfirm({
        ...costCenter,
        ...values,
      } as CostCenter);
      if (!isFromSelection) setShowSuccess(true);
    } catch (error) {
      if (error.error) {
        if (error.error.indexOf('already') > -1) {
          toast.add({
            type: 'danger',
            message: t('costCenter.errors.alreadyExists', {
              costCenterName: values?.name,
              costCenterCode: values?.code,
            }),
          });
        }
      } else {
        toast.add({
          type: 'danger',
          message: extractErrorMessage(error) || t('defaultError.default'),
        });
      }
    }
  };

  const handleNavigateHistory = () => {
    if (isFromSelection) {
      if (onCloseFormCostCenter) onCloseFormCostCenter();
    } else {
      history.push('/costCenterList');
    }
  };

  return (
    <Container>
      <Mobile>
        <AppBar goBack={handleNavigateHistory}>
          <h5>{t(!costCenter?.id ? 'costCenter.header.create' : 'costCenter.header.edit')}</h5>
        </AppBar>
      </Mobile>
      {loading ? (
        <Loader fullScreen />
      ) : (
        <>
          <Desktop>
            <ScreenHeader title={t('userMenu.costCenterTitle')} subtitle={t('userMenu.costCenterDetail')} />
          </Desktop>

          <Formik
            isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
            initialValues={initialValues as CostCenter}
            validationSchema={validationSchema}
            onSubmit={(values: CostCenter) => handleConfirm(values)}
          >
            {({ dirty, isSubmitting }: FormikValues) => (
              <>
                <FormikSubmitErrorEffect />

                <Form isEdition={isEdit && dirty}>
                  <Fields>
                    <Section title={t('costCenter.details.title')} details={htmlT(t, 'costCenter.details.text')}>
                      <Field name="name" label={t('costCenter.details.name.label')}>
                        <Input placeholder={t('costCenter.details.name.placeHolder')} />
                      </Field>
                      <Field name="code" label={t('costCenter.details.code.label')}>
                        <Input placeholder={t('costCenter.details.code.placeHolder')} />
                      </Field>
                    </Section>
                  </Fields>
                  <SubmitButton isEdit={isEdit} dirty={dirty} loading={submitting || isSubmitting}>
                    {t(isEdit ? 'costCenter.buttons.edit' : 'costCenter.buttons.confirm')}
                  </SubmitButton>
                </Form>
              </>
            )}
          </Formik>

          <ResponseScreen
            show={showSuccess}
            background={BackgroundTypes.Mountain}
            text={t('costCenterSuccess.title')}
            subText={!!costCenter?.id ? t('costCenterSuccess.textEdit') : t('costCenterSuccess.text')}
            buttonOnClick={() => history.push('/costCenterList')}
            buttonText={t('costCenterSuccess.buttonText')}
          />
        </>
      )}
    </Container>
  );
};

export default CostCenterForm;
